import "./WelcomeModal.scss"

import React, { useEffect, useState } from "react"
import { Modal, Icon } from "@fider/components"
import { useFider } from "@fider/hooks"
import { uploadedImageURL } from "@fider/services/utils"
import IconX from "@fider/assets/images/heroicons-x.svg"
import { i18n } from "@lingui/core"
import { Trans } from "@lingui/react/macro"

// Tùy biến KHCN: poster chào mừng (sự kiện, thông báo) hiện đúng một lần mỗi lần đăng nhập.
// Server đặt cookie fider_welcome khi đăng nhập thành công; trình duyệt đọc rồi xóa ngay nên tải lại
// trang không hiện lại, đăng xuất rồi đăng nhập lại mới hiện.
//
// Nguồn poster theo thứ tự ưu tiên:
//   1) ảnh quản trị viên tải lên trong Cài đặt chung (tenant.welcomePosterBlobKey)
//   2) file mặc định để sẵn trong mã nguồn tại static/welcome-poster.(png|jpg)
//      -> phục vụ ở /static/assets/welcome-poster.(png|jpg), chỉ cần thả file vào là chạy.
// Ảnh được thử tải trước; không có ảnh nào tải được thì không hiện modal.

const WELCOME_COOKIE = "fider_welcome"
const DEFAULT_POSTER_NAMES = ["welcome-poster.png", "welcome-poster.jpg", "welcome-poster.jpeg", "welcome-poster.webp"]

const readAndClearWelcomeCookie = (): boolean => {
  if (typeof document === "undefined") {
    return false
  }
  const found = document.cookie.split(";").some((part) => part.trim().startsWith(`${WELCOME_COOKIE}=`))
  if (found) {
    document.cookie = `${WELCOME_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`
  }
  return found
}

const givenNameOf = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/)
  return parts[parts.length - 1] || fullName
}

// Trả về URL ảnh đầu tiên tải được trong danh sách, hoặc undefined nếu không có
const firstLoadableImage = (urls: string[]): Promise<string | undefined> =>
  new Promise((resolve) => {
    const tryAt = (index: number) => {
      if (index >= urls.length) {
        resolve(undefined)
        return
      }
      const img = new Image()
      img.onload = () => (img.naturalWidth > 0 ? resolve(urls[index]) : tryAt(index + 1))
      img.onerror = () => tryAt(index + 1)
      img.src = urls[index]
    }
    tryAt(0)
  })

export const WelcomeModal = () => {
  const fider = useFider()
  const [isOpen, setIsOpen] = useState(false)
  const [posterURL, setPosterURL] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!fider.session.isAuthenticated) {
      return
    }
    // Trang điểm danh thành công đã hiển thị poster + chương trình: tiêu thụ cookie, không bật modal đè lên
    if (fider.session.page === "Checkin/Success.page") {
      readAndClearWelcomeCookie()
      return
    }
    if (!readAndClearWelcomeCookie()) {
      return
    }
    const adminPoster = uploadedImageURL(fider.session.tenant.welcomePosterBlobKey, 1600)
    // Chống cache 365 ngày của thư mục static: đổi poster cùng tên vẫn cập nhật trong ngày
    const day = new Date().toISOString().slice(0, 10)
    const defaults = DEFAULT_POSTER_NAMES.map((name) => `${fider.settings.assetsURL}/static/assets/${name}?d=${day}`)
    const candidates = adminPoster ? [adminPoster, ...defaults] : defaults

    let cancelled = false
    firstLoadableImage(candidates).then((url) => {
      if (!cancelled && url) {
        setPosterURL(url)
        setIsOpen(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (!posterURL) {
    return null
  }

  const close = () => setIsOpen(false)
  const greeting = i18n._({ id: "khcn.welcome.greeting", message: "Welcome back, {name}!", values: { name: givenNameOf(fider.session.user.name) } })

  return (
    <Modal.Window isOpen={isOpen} onClose={close} size="fluid" className="c-welcome-modal">
      <Modal.Content>
        <div className="c-welcome-modal__body">
          <button type="button" className="c-welcome-modal__close" onClick={close} aria-label={i18n._({ id: "khcn.welcome.close", message: "Close" })}>
            <Icon sprite={IconX} />
          </button>
          <img className="c-welcome-modal__poster" src={posterURL} alt={greeting} />
          <div className="c-welcome-modal__footer">
            <span className="c-welcome-modal__eyebrow">
              <Trans id="khcn.welcome.eyebrow">Welcome</Trans>
            </span>
            <span className="c-welcome-modal__greeting">{greeting}</span>
          </div>
        </div>
      </Modal.Content>
    </Modal.Window>
  )
}
